import type { FC, Ref } from 'react'
import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import {
  CwsButton,
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
  CwsH2,
  CwsModal,
  CwsModalBody,
  CwsModalFooter,
  CwsP,
  CwsSection,
  CwsSpan,
  CwsAligner,
} from '@components/cws'

// @ts-ignore
import { modal } from 'cms-web-styleguide/dist/utils/utils'
import type { ProductItemBaseProps } from '@components/product'
import { CategoryTitle, Parameter, ProductCard, ProductGrid } from '@components/product'
import type { KeyLabelOption } from '@ui/components/Selector/Selector'
import s from './CategoryView.module.scss'
import clsx from 'clsx'
import { Pagination } from '@components/common/Pagination'
import { useMediaLess } from '@ui/hooks/useMediaLess'
import type { ManufacturerFacet, ProductSearchResult } from '@model/product'
import { Loader } from '@ui/components/Loader'
import * as R from 'ramda'
import { InView } from 'react-intersection-observer'
import { FilterTag } from '../FilterTag'
import { useTranslate } from '@hooks/useTranslation'
import type { FilterValue } from '@context/category-filters'
import { useCategoryFilters } from '@context/category-filters'
import axios from 'axios'
import type { OrderKey } from '@model/product/ProductSearchResult'
import { KK_ORDER_BY_VALUES } from '@model/product/ProductSearchResult'
import { ProductTableListItem } from '@components/product/ProductTableListItem'
import { ProductTableList } from '@components/product/ProductTableList'
import { parseProductSearchQueryToApiFilters } from '@utils/product-api'
import type { CatalogFromContent, ImagePageBanner } from '@api/content'
import { useAppState } from '@context/state'
import { makeConfig } from '@components/category/HomePageCollection/config'
import type { Locale } from '@model/locales'
import { KK_LANG_IDS } from '@model/locales'
import type { CollectionType } from '@api/categories'
import { Banners } from '@components/category/CategoryView/Banners'
import { useCurrency } from '@hooks/useCurrency'
import { getPriceValueWithCurrency, revertBackToEURCurrency } from '@utils/currency'
import type { Currency } from '@model/product/PriceInfo'
import { getSlug, unSlug } from '@utils/slugs'
import { CategoryFilterGroup } from '@components/category/CategoryView/CategoryFilterGroup'
import { useResize } from '@ui/hooks/useResize'
import { colSize } from '@utils/colSize'
import StickyBox from 'react-sticky-box'
import type { Catalog } from '@model/pricelist/Catalog'
import useSWR from 'swr'
import NothingFound from '@components/common/NothingFound'
import { useModalState } from '@context/ModalContext'
import { MODAL_ID } from '@components/category/ShipFilter/ShipFilter'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'
import debounce from 'lodash.debounce'
import { getFormattedPrice } from '@utils/price'
import { sessionStorageHelper } from '@utils/localStorageHelper'
import { getParentsArray } from '@utils/getParentCategoryTree'
import { useBreadCrumbsState } from '@components/BreadCrumbs/State/state'
import { Breadcrumbs } from '@components/BreadCrumbs/State/reducer'
import { BannerWrapper } from '@components/category/BannerWrapper/BannerWrapper'
import { useInitialData } from '@context/initialDataContext'
import { PERCENTAGE_OF_POINT_USED } from '../../../constants/product'
import { MOBIL_SIZE_FILTER, SidePanelWrapper } from '@components/category/CategorySidePanel/SidePanelWrapper'
import { setQuery } from '@utils/setQuery'

export const stringOfCustomFacets = (facets: FilterValue[]) => facets.map(R.prop('id')).join(';')

export type CategoryPagePayload = {
  category: ProductSearchResult
  banners: ImagePageBanner[]
  currentPage: number
  catalogId?: Catalog
}

interface ProductsPartProps {
  query?: string
  page: number
  itemsPerPageValue: string
  perPage?: number
  onDataLoaded?: (data: CategoryPagePayload) => any
  onDataStartLoading?: () => any
  onDataLoadingCancelled?: () => void
  ProductItemComponent: FC<ProductItemBaseProps>
  ProductsContainerComponent: FC<any>
  id?: number
  catalogId?: Catalog
  onInViewChange?: (page: number, inView: boolean) => any
  customFacets?: FilterValue[]
  searchTerm?: string
  collectionName?: string
  locale?: string
  minPrice?: number | null
  maxPrice?: number | null
  colSize: string
  manufacturerId?: number
  manufacturerIds?: number[]
  manufacturerName?: string
}

const EMPTY_SEARCH_FOR_PRODUCTS_DATA: ProductSearchResult = {
  minPrice: 0,
  maxPrice: 0,
  customFacets: [],
  manufacturerFacets: [],
  productArray: [],
  categoryFacets: [],
  taxMultiplier: 0,
  totalNumProducts: 0,
}

const MAGIC_INITIAL_ITEMS_PER_PAGE_STR = '30'

const ProductsPart = forwardRef((props: ProductsPartProps, ref: Ref<HTMLDivElement>) => {
  const {
    query,
    page,
    onDataLoaded,
    onDataStartLoading,
    onDataLoadingCancelled,
    itemsPerPageValue,
    ProductItemComponent,
    ProductsContainerComponent,
    id,
    collectionName,
    onInViewChange,
    customFacets = [],
    searchTerm,
    locale,
    colSize,
    catalogId,
    manufacturerId,
    manufacturerIds,
  } = props

  const [loaded, setLoaded] = useState(true)
  const [data, setData] = useState<ProductSearchResult | null>(null)
  const [banners, setBanners] = useState<ImagePageBanner[]>([])

  const { currency } = useCurrency()
  const {
    initialData: { currencies },
  } = useInitialData()
  const loadingRef = useRef(false)
  const loadingStartedRef = useRef(false)

  const searchValue = searchTerm

  const {
    state: { customer },
  } = useAppState()
  const { data: availableCOPoints = 1 } = useSWR<number>(customer && '/api/customer/load-clubone-points')
  const addCOPoint = (data: CategoryPagePayload): CategoryPagePayload => {
    if (!customer) return data
    const products = [...data.category.productArray].map((product) => ({
      ...product,
      [PERCENTAGE_OF_POINT_USED]: `${+product[PERCENTAGE_OF_POINT_USED] * availableCOPoints}`,
    }))
    return { ...data, category: { ...data.category, productArray: products } }
  }
  useEffect(() => {
    const run = () => {
      if (!id && !searchValue && !collectionName && !manufacturerId) {
        return
      }

      if (loadingRef.current) {
        return
      }

      loadingRef.current = true
      loadingStartedRef.current = true

      onDataStartLoading?.()

      const urlSearchParams = new URLSearchParams(query)

      urlSearchParams.set('page', `${page}`)
      urlSearchParams.set('locale', `${locale}`)
      urlSearchParams.set('count', itemsPerPageValue)

      if (urlSearchParams.get('minPrice') && currency === 'SEK') {
        const sekValue = parseFloat(urlSearchParams.get('minPrice') as string)
        urlSearchParams.set('minPrice', `${revertBackToEURCurrency(sekValue, currencies)}`)
      }

      if (urlSearchParams.get('maxPrice') && currency === 'SEK') {
        const sekValue = parseFloat(urlSearchParams.get('maxPrice') as string)
        urlSearchParams.set('maxPrice', `${revertBackToEURCurrency(sekValue, currencies)}`)
      }

      urlSearchParams.sort()
      const searchQuery: string = urlSearchParams ? `?${urlSearchParams}` : ''

      if (Boolean(id) && !Boolean(manufacturerId)) {
        axios
          .post<CategoryPagePayload>(`/api/category/${id}${searchQuery}`, {
            catalogId,
            facets: [...new Set(customFacets.filter((f) => !!f.group).map((f) => +f.group!))].map((facetNumber) => ({
              facetNumber,
              facetConstraints: customFacets.filter((f) => f.group === facetNumber.toString()).map((f) => f.id),
            })),
          })
          .then(({ data }) => {
            const dataWithCOPoint = addCOPoint(data)
            setData(dataWithCOPoint.category)
            setBanners(dataWithCOPoint.banners)
            setLoaded(true)
            onDataLoaded?.(dataWithCOPoint)
          })
          .catch((error) => {
            const code = axios.isAxiosError(error) ? error.code : undefined
            const message = axios.isAxiosError(error) ? error.message : String(error)
            console.error('Error loading category by id', { code, message })
            setLoaded(false)
            setBanners([])
            setData(EMPTY_SEARCH_FOR_PRODUCTS_DATA)
            onDataLoaded?.({
              category: EMPTY_SEARCH_FOR_PRODUCTS_DATA,
              banners: [],
              currentPage: 1,
            })
          })
          .finally(() => {
            loadingRef.current = false
            loadingStartedRef.current = false
          })
      } else if (Boolean(collectionName)) {
        axios
          .post<CategoryPagePayload>(`/api/collection/${collectionName}${searchQuery}`, {
            catalogId,
            facets: [...new Set(customFacets.filter((f) => !!f.group).map((f) => +f.group!))].map((facetNumber) => ({
              facetNumber,
              facetConstraints: customFacets.filter((f) => f.group === facetNumber.toString()).map((f) => f.id),
            })),
          })
          .then(({ data }) => {
            const dataWithCOPoint = addCOPoint(data)
            setData(dataWithCOPoint.category)
            setLoaded(true)
            onDataLoaded?.({
              category: dataWithCOPoint.category,
              banners: [],
              currentPage: dataWithCOPoint.currentPage,
            })
          })
          .catch((error) => {
            const code = axios.isAxiosError(error) ? error.code : undefined
            const message = axios.isAxiosError(error) ? error.message : String(error)
            console.error('Opening a product category failed', { code, message })
            setLoaded(false)
            setData(EMPTY_SEARCH_FOR_PRODUCTS_DATA)
            onDataLoaded?.({
              category: EMPTY_SEARCH_FOR_PRODUCTS_DATA,
              banners: [],
              currentPage: 1,
            })
          })
          .finally(() => {
            loadingRef.current = false
            loadingStartedRef.current = false
          })
      } else if (Boolean(manufacturerId)) {
        axios
          .post<CategoryPagePayload>(`/api/brand/${manufacturerId}${searchQuery}`, {
            catalogId,
            manufacturerIds,
            facets: [...new Set(customFacets.filter((f) => !!f.group).map((f) => +f.group!))].map((facetNumber) => ({
              facetNumber,
              facetConstraints: customFacets.filter((f) => f.group === facetNumber.toString()).map((f) => f.id),
            })),
          })
          .then(({ data }) => {
            const dataWithCOPoint = addCOPoint(data)
            setData(dataWithCOPoint.category)
            setLoaded(true)
            onDataLoaded?.({
              category: dataWithCOPoint.category,
              banners: [],
              currentPage: dataWithCOPoint.currentPage,
            })
          })
          .catch((error) => {
            const code = axios.isAxiosError(error) ? error.code : undefined
            const message = axios.isAxiosError(error) ? error.message : String(error)
            console.error('Opening a Brands page failed', { code, message })
            setLoaded(false)
            setData(EMPTY_SEARCH_FOR_PRODUCTS_DATA)
            onDataLoaded?.({
              category: EMPTY_SEARCH_FOR_PRODUCTS_DATA,
              banners: [],
              currentPage: 1,
            })
          })
          .finally(() => {
            loadingRef.current = false
            loadingStartedRef.current = false
          })
      } else {
        const filtersFromQuery = query ? parseProductSearchQueryToApiFilters(query) : {}
        const languageId = KK_LANG_IDS[locale as Locale]

        axios
          .post<CategoryPagePayload>(`/api/search${searchQuery}`, {
            ...filtersFromQuery,
            catalogId,
            languageId,
            tagGroups:
              [...new Set(customFacets.filter((f) => !!f.group).map((f) => +f.group!))].map((facetNumber) => ({
                facetNumber,
                facetConstraints: customFacets.filter((f) => f.group === facetNumber.toString()).map((f) => f.id),
              })) ?? undefined,
            searchText: searchValue,
            // ...searchOptions,
          })
          .then(({ data }) => {
            const dataWithCOPoint = addCOPoint(data)
            setData(dataWithCOPoint.category)
            setLoaded(true)
            onDataLoaded?.({
              category: dataWithCOPoint.category,
              banners: [],
              currentPage: dataWithCOPoint.currentPage,
            })
          })
          .catch((error) => {
            const code = axios.isAxiosError(error) ? error.code : undefined
            const message = axios.isAxiosError(error) ? error.message : String(error)
            console.error('Search for products failed', { code, message })
            setLoaded(false)
            setData(EMPTY_SEARCH_FOR_PRODUCTS_DATA)
            onDataLoaded?.({
              category: EMPTY_SEARCH_FOR_PRODUCTS_DATA,
              banners: [],
              currentPage: 1,
            })
          })
          .finally(() => {
            loadingRef.current = false
            loadingStartedRef.current = false
          })
        // We search products
      }
    }

    const debounced = debounce(run, 750)
    debounced()

    return () => {
      debounced.cancel()
      if (loadingStartedRef.current || loadingRef.current) {
        loadingRef.current = false
        loadingStartedRef.current = false
        onDataLoadingCancelled?.()
      }
    }
  }, [
    id,
    collectionName,
    query,
    page,
    stringOfCustomFacets(customFacets),
    locale,
    searchValue,
    itemsPerPageValue,
    catalogId,
    manufacturerId,
  ])

  const categoryData = data
  const productArray = categoryData?.productArray
  const onInViewChangeRef = useRef(onInViewChange)

  const node = useMemo(() => {
    return (
      <>
        <div ref={ref}></div>

        <InView
          as="div"
          threshold={0.1}
          onChange={(inView) => {
            onInViewChangeRef?.current?.(page, inView)
          }}
        />
        {/*<CwsAligner>*/}
        {productArray?.map((product, idx) => (
          <CwsGridCol col={colSize} key={`${id}_${page}_${product.id}`}>
            <ProductCard listName="product_category" product={product} index={idx + 1} />
          </CwsGridCol>
        ))}
        {/*</CwsAligner>*/}
      </>
    )
  }, [productArray, ProductItemComponent, ProductsContainerComponent, id, page, colSize])

  return node
})

const ProductsPartMemorized = React.memo(ProductsPart, (prevProps, nextProps) => {
  return (
    prevProps.manufacturerIds === nextProps.manufacturerIds &&
    prevProps.id === nextProps.id &&
    prevProps.manufacturerId === nextProps.manufacturerId &&
    (prevProps.query ?? '') === (nextProps.query ?? '') &&
    prevProps.searchTerm === nextProps.searchTerm &&
    prevProps.itemsPerPageValue === nextProps.itemsPerPageValue &&
    prevProps.locale === nextProps.locale &&
    prevProps.page === nextProps.page &&
    prevProps.minPrice === nextProps.minPrice &&
    prevProps.maxPrice === nextProps.maxPrice &&
    prevProps.onDataLoadingCancelled === nextProps.onDataLoadingCancelled &&
    prevProps.colSize === nextProps.colSize
  )
})

ProductsPart.displayName = 'ProductsPart'

type ViewType = 'grid' | 'list'

const viewTypeContainerComponentMap = {
  grid: ProductGrid,
  list: ProductTableList,
  // list: ProductList,
}

type ViewTypeProductItemComponentMap = {
  [key in ViewType]: FC<ProductItemBaseProps>
}

const viewTypeProductItemComponentMap: ViewTypeProductItemComponentMap = {
  grid: ProductCard,
  list: ProductTableListItem,
}

const itemsPerPageOptions: KeyLabelOption[] = [30, 60, 120].map<KeyLabelOption>((val) => ({
  key: val.toString(),
  label: val.toString(),
}))

const orderByOptions: KeyLabelOption[] = [
  {
    key: 'none',
    label: '--',
    translationKey: 'search.page.orderby.none',
  },
  { key: 'priceAscending', translationKey: 'search.page.orderby.priceasc', label: 'Price: ascending' },
  { key: 'priceDescending', translationKey: 'search.page.orderby.pricedesc', label: 'Price: descending' },
  { key: 'nameToZ', translationKey: 'search.page.orderby.nameasc', label: 'Name: a to z' },
  { key: 'nameToA', translationKey: 'search.page.orderby.namedesc', label: 'Name: z to a' },
  { key: 'addedDescending', translationKey: 'search.page.orderby.addeddesc', label: 'Date Added' },
  // { key: 'byBestSellers', translationKey: 'search.page.orderby.purchaseddesc', label: 'Sort by best sellers' },
  // { key: 'recommended', translationKey: 'search.page.orderby.recommended', label: 'Recommended' },
]

export interface CategoryViewProps {
  categoryData?: ProductSearchResult
  banners: ImagePageBanner[]
  currentPage: number
  forcedTitle?: string
  categoryNameOrSearchTerm?: string
  categoryName?: string
  categoryDescription?: string
  catalogGroups?: Record<string, CatalogFromContent[]>
  manufacturerId?: number
  manufacturerIds?: number[]
  manufacturerName?: string
  jsxData?: string
}

const CategoryView: FC<CategoryViewProps> = ({
  categoryData,
  forcedTitle,
  categoryNameOrSearchTerm,
  banners,
  categoryDescription,
  catalogGroups,
  manufacturerId,
  manufacturerIds,
  manufacturerName,
  jsxData,
  currentPage,
}) => {
  const { manufacturerFacets, categoryFacets, customFacets } = categoryData ?? {}
  const { translate } = useTranslate()
  const router = useRouter()
  const isRouterReady = router.isReady

  const { deviceSize } = useResize()

  const { slug, term, name: collectionName, minPrice: minPriceQuery, maxPrice: maxPriceQuery } = router.query

  const {
    brandsData,
    toggleBrandFilter,
    queryParams,
    setCategoryData,
    setCategoryBanners,
    setSortOrder,
    customs,
    toggleCustom,
    resetAll,
    minPrice,
    maxPrice,
    setMinPrice,
    setMaxPrice,
    resetPriceRange,
  } = useCategoryFilters()

  const id = typeof slug === 'string' ? unSlug(slug) : undefined
  const {
    locale,
    query: { catalogId = '' },
  } = router
  const queryString = queryParams

  const pageIdentifier = useMemo(() => {
    if (slug) return slug
    if (term) return term
    if (collectionName) return `collection-${collectionName}`
    if (router.asPath.split('/').includes('products')) return 'products'

    return 'default'
  }, [slug, collectionName, term, router.asPath])

  const [totalResults, setTotalResults] = useState<number | null>(null)
  const [calculatedTotalPages, setCalculatedTotalPages] = useState<number | null>(null)
  const [latestLoadedPage, setLatestLoadedPage] = useState<number>(0)
  const [isFetching, setIsFetching] = useState(false)
  const loadingStartRef = useRef<string>('')
  const expectedPagesRef = useRef<number>(1)
  const [page, setPage] = useState<number>(1)
  const [activePages, setActivePages] = useState<number>(1)

  useEffect(() => {
    if (!isRouterReady) return

    const queryPage = +(router.query.page ?? '1')
    const queryTotalPages = +(router.query.totalPages ?? '1')

    let newPage = Math.max(1, queryPage)
    let newActivePages = Math.max(1, queryTotalPages)

    if (calculatedTotalPages) {
      newActivePages = Math.min(newActivePages, calculatedTotalPages)
      newPage = Math.min(newPage, calculatedTotalPages)
    } else {
      const MAX_INITIAL_PAGES = 5
      newActivePages = Math.min(newActivePages, MAX_INITIAL_PAGES)
      newPage = Math.min(newPage, MAX_INITIAL_PAGES)
    }

    setPage((prevPage) => (prevPage !== newPage ? newPage : prevPage))
    setActivePages((prevActivePages) => (prevActivePages !== newActivePages ? newActivePages : prevActivePages))
  }, [isRouterReady, router.query.page, router.query.totalPages, calculatedTotalPages])

  useEffect(() => {
    if (!isRouterReady || !calculatedTotalPages) return

    let needsUpdate = false
    let newPage = page
    let newActivePages = activePages

    if (page < 1) {
      newPage = 1
      needsUpdate = true
    } else if (page > calculatedTotalPages) {
      newPage = calculatedTotalPages
      needsUpdate = true
    }

    if (activePages > calculatedTotalPages) {
      newActivePages = calculatedTotalPages
      needsUpdate = true
    } else if (activePages < 1) {
      newActivePages = 1
      needsUpdate = true
    }

    if (newPage > newActivePages) {
      newPage = newActivePages
      needsUpdate = true
    }

    if (needsUpdate) {
      setPage(newPage)
      setActivePages(newActivePages)

      const { page: _, totalPages: __, ...restQuery } = router.query
      const currentPageInUrl = router.query.page
      const newQuery: Record<string, string | string[] | undefined> = {
        ...restQuery,
        // Preserve page if it was in URL or if it's > 1
        ...((currentPageInUrl || newPage > 1) && { page: newPage.toString() }),
        ...(newActivePages > 1 && { totalPages: newActivePages.toString() }),
      }

      router
        .replace(
          {
            pathname: router.pathname,
            query: newQuery,
          },
          undefined,
          { scroll: false }
        )
        .catch(() => {})
    }
  }, [isRouterReady, calculatedTotalPages, page, activePages, router])

  useEffect(() => {
    if (!isRouterReady || !page || page < 1) return

    let cancelled = false
    let timeoutId: NodeJS.Timeout | null = null

    const scrollToPage = () => {
      if (cancelled) return false

      const pageIndex = page - 1
      const pageRef = pagesRefs.current[pageIndex]

      if (!pageRef) return false

      const isPage1 = page === 1
      const isDataLoaded = isPage1 ? !isFetching : latestLoadedPage >= page

      if (!isDataLoaded) return false

      const hasContent = pageRef.nextElementSibling !== null

      if (hasContent || (isPage1 && !isFetching)) {
        requestAnimationFrame(() => {
          if (!cancelled) {
            pageRef.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
        return true
      }

      return false
    }

    let attempts = 0
    const maxAttempts = 50 // Try for up to 5 seconds (50 * 100ms) to account for data loading

    const tryScroll = () => {
      if (cancelled) return

      attempts++
      const success = scrollToPage()

      if (!success && attempts < maxAttempts && (page <= activePages || isFetching)) {
        timeoutId = setTimeout(tryScroll, 100)
      }
    }

    timeoutId = setTimeout(tryScroll, 200)

    return () => {
      cancelled = true
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [page, isRouterReady, activePages, isFetching, latestLoadedPage])

  useEffect(() => {
    if (!isRouterReady || isFetching) return

    const lastViewedProductId = sessionStorageHelper.get('lastViewedProductId')
    const lastViewedProductPath = sessionStorageHelper.get('lastViewedProductPath')

    if (lastViewedProductId && lastViewedProductPath && router.asPath === lastViewedProductPath) {
      const scrollToProduct = () => {
        const productElement = document.querySelector(`[data-product-id="${lastViewedProductId}"]`)
        if (productElement) {
          productElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          sessionStorageHelper.remove('lastViewedProductId')
          sessionStorageHelper.remove('lastViewedProductPath')
        }
      }

      const timeoutId = setTimeout(scrollToProduct, 500)
      return () => clearTimeout(timeoutId)
    }
  }, [isRouterReady, isFetching, router.asPath, latestLoadedPage])

  const [loadedResultsCount, setLoadedResultsCount] = useState(0)

  const itemsPerPageFromStorage = sessionStorageHelper.get('itemsPerPage') || MAGIC_INITIAL_ITEMS_PER_PAGE_STR
  const [itemsPerPageValue, setItemsPerPageValue] = useState<string>(itemsPerPageFromStorage)

  const { currency } = useCurrency()
  const {
    initialData: { currencies },
  } = useInitialData()

  useEffect(() => {
    sessionStorageHelper.set('itemsPerPage', itemsPerPageValue)
    if (totalResults) {
      const perPage = parseFloat(itemsPerPageValue || MAGIC_INITIAL_ITEMS_PER_PAGE_STR)
      const newCalculatedTotalPages = Math.ceil(totalResults / perPage)
      setCalculatedTotalPages(newCalculatedTotalPages)
    }
  }, [itemsPerPageValue, totalResults])

  const handleDataLoaded = useCallback(
    (data: CategoryPagePayload) => {
      const perPage = parseFloat(
        itemsPerPageValue || sessionStorageHelper.get('itemsPerPage') || MAGIC_INITIAL_ITEMS_PER_PAGE_STR
      )
      const newCalculatedTotalPages = Math.ceil(data.category.totalNumProducts / perPage)

      setTotalResults(data.category.totalNumProducts)
      setCalculatedTotalPages(newCalculatedTotalPages)

      if (activePages > newCalculatedTotalPages) {
        const correctedActivePages = newCalculatedTotalPages
        setActivePages(correctedActivePages)

        let correctedPage = page
        if (page > correctedActivePages) {
          correctedPage = correctedActivePages
          setPage(correctedPage)
        }

        const { page: _, totalPages: __, ...restQuery } = router.query
        const currentPageInUrl = router.query.page
        const newQuery: Record<string, string | string[] | undefined> = {
          ...restQuery,
          // Preserve page if it was in URL or if it's > 1
          ...((currentPageInUrl || correctedPage > 1) && { page: correctedPage.toString() }),
          ...(correctedActivePages > 1 && { totalPages: correctedActivePages.toString() }),
        }
        router
          .replace(
            {
              pathname: router.pathname,
              query: newQuery,
            },
            undefined,
            { scroll: false }
          )
          .catch(() => {})
      }

      setLatestLoadedPage((prevLoadedPage) => {
        const newLoadedPage = prevLoadedPage + 1
        const expectedPages = expectedPagesRef.current || validatedActivePagesRef.current || activePages
        if (newLoadedPage >= expectedPages) {
          setIsFetching(false)
        }
        return newLoadedPage
      })
      setLoadedResultsCount((prevValue) => {
        if (data.category?.productArray?.length) {
          const currentPageFromResponse = data.currentPage
          return data.category?.productArray?.length + (currentPageFromResponse - 1) * perPage
        } else return 0
      })
      setCategoryData(data.category)
      setCategoryBanners(data.banners)

      const categoryMinPrice = getPriceValueWithCurrency(data.category.minPrice || 0, currency, currencies)
      const categoryMaxPrice = getPriceValueWithCurrency(data.category.maxPrice || 0, currency, currencies)

      if (minPrice && minPrice < categoryMinPrice) {
        setMinPrice(null)
        const { minPrice: _, maxPrice: __, page, totalPages, ...restQuery } = router.query
        router
          .replace(
            {
              pathname: router.pathname,
              query: { ...restQuery },
            },
            undefined,
            { scroll: false }
          )
          .catch(() => {})
      }
      if (maxPrice && maxPrice > categoryMaxPrice) {
        setMaxPrice(null)
        const { minPrice: _, maxPrice: __, page, totalPages, ...restQuery } = router.query
        router
          .replace(
            {
              pathname: router.pathname,
              query: { ...restQuery },
            },
            undefined,
            { scroll: false }
          )
          .catch(() => {})
      }
    },
    [
      itemsPerPageValue,
      setCategoryData,
      setCategoryBanners,
      minPrice,
      maxPrice,
      setMinPrice,
      setMaxPrice,
      activePages,
      router,
      currency,
      currencies,
    ]
  )

  const [endReached, setEndReached] = useState(false)

  const languageId = KK_LANG_IDS[locale as Locale]
  const collectionsMap = makeConfig(languageId, catalogId as Catalog)

  const pagesRefs = useRef<HTMLDivElement[]>([])

  const goToPage = useCallback(
    (newPage: number) => () => {
      const maxPage = calculatedTotalPages || activePages
      const validPage = Math.max(1, Math.min(newPage, maxPage))

      setPage(validPage)

      const currentQuery = { ...router.query }
      currentQuery.page = validPage.toString()

      requestAnimationFrame(() => {
        const pageRef = pagesRefs.current[newPage - 1]
        if (pageRef) {
          pageRef.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
      router.push(
        {
          pathname: router.pathname,
          query: currentQuery,
        },
        undefined,
        { shallow: true }
      )
    },
    [router, calculatedTotalPages, activePages]
  )

  const loading = isFetching

  const [title, setTitle] = useState<string>('')

  const {
    initialData: { categories, catalogs },
  } = useInitialData()

  useEffect(() => {
    if (loading) {
      return
    }

    if (collectionName) {
      const collectionNameKey = collectionsMap[collectionName as CollectionType].title
      return setTitle(translate(collectionNameKey))
    }

    if (!categoryNameOrSearchTerm) {
      setTitle(
        forcedTitle ?? categoryData?.categoryFacets?.[0]?.name ?? (translate('w2.category.missing.message') as string)
      )
    }
  }, [categoryData, loading, forcedTitle, categoryNameOrSearchTerm, collectionName, translate])
  const { dispatch: breadCrumbDispatch } = useBreadCrumbsState()
  const currentTreeCategory = useMemo(() => getParentsArray(categories, +(id || 0)), [categories, locale, router.query])

  useEffect(() => {
    if (collectionName) {
      breadCrumbDispatch({
        type: 'setBreadcrumbs',
        payload: [
          { href: '/', title: translate('breadcrumb.home') },
          {
            href: `/${locale}/collection/${collectionName}`,
            title: translate(collectionsMap[collectionName as CollectionType].title),
          },
        ],
      })
    }
    if (currentTreeCategory.length) {
      const breadcrumbsList: Breadcrumbs[] = currentTreeCategory[0].map(({ id, name }) => {
        const slug = getSlug(`${name}`, id)
        let href = router.query.catalogId
          ? `/${locale}/${router.query.catalogId}/category/${slug}`
          : `/${locale}/category/${slug}`

        return { href, title: name }
      })
      if (catalogs.length && router.query.catalogId) {
        const currentCatalog = catalogs.find((item) => item.id === router.query.catalogId)
        if (currentCatalog) {
          breadCrumbDispatch({
            type: 'setBreadcrumbs',
            payload: [
              { href: '/', title: translate('breadcrumb.home') },
              { href: `/${locale}/${router.query.catalogId}`, title: currentCatalog.title },
              ...breadcrumbsList,
            ],
          })
        }
      } else {
        breadCrumbDispatch({
          type: 'setBreadcrumbs',
          payload: [{ href: '/', title: translate('breadcrumb.home') }, ...breadcrumbsList],
        })
      }
    }
  }, [currentTreeCategory, router, collectionName])
  const isMobile = useMediaLess(MOBIL_SIZE_FILTER)
  const isLaptop = !isMobile
  const [viewType, setViewType] = useState<ViewType>('grid')

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true)

  useEffect(() => {
    const isBrands = router.asPath.split('/').includes('brands')
    if (isBrands) {
      setIsSidePanelOpen(false)
    }
  }, [router])
  const [ProductsContainer, ProductItem] = useMemo(
    () => [viewTypeContainerComponentMap[viewType], viewTypeProductItemComponentMap[viewType]],
    [viewType]
  )

  const pagesTotal = calculatedTotalPages

  const currencyRef = useRef<Currency>()
  const maxMinPrices = useRef<number[]>([0, 0])
  useEffect(() => {
    if (currencyRef.current && currencyRef.current !== currency) {
      setMinPrice(null)
      setMaxPrice(null)
    }

    currencyRef.current = currency
  }, [currency, setMinPrice, setMaxPrice])
  useEffect(() => {
    if (!categoryData?.minPrice && !categoryData?.maxPrice) return
    if (maxMinPrices.current[0] !== categoryData?.minPrice || maxMinPrices.current[1] !== categoryData?.maxPrice) {
      maxMinPrices.current = [categoryData?.minPrice, categoryData?.maxPrice]
      return
    }
  }, [categoryData])
  const minAvailablePrice = getPriceValueWithCurrency(maxMinPrices.current[0], currency, currencies)
  const maxAvailablePrice = getPriceValueWithCurrency(maxMinPrices.current[1], currency, currencies)

  const sidePanelNode = useMemo(
    () => (
      <MemorizedSidePanel
        currentMaxPrice={maxPrice ?? 0}
        currentMinPrice={minPrice ?? 0}
        resetAll={resetAll}
        manufacturerFacets={manufacturerFacets ?? []}
        categoryFacets={categoryFacets ?? []}
        categoryId={categoryData ? parseInt((id as string) ?? '') : 0}
        minAvailablePrice={minAvailablePrice ?? 0}
        maxAvailablePrice={maxAvailablePrice ?? 0}
        customFacets={customFacets ?? []}
        loading={!categoryData}
        catalogGroups={catalogGroups}
      />
    ),
    [
      manufacturerFacets,
      categoryFacets,
      id,
      minAvailablePrice,
      maxAvailablePrice,
      categoryData,
      customFacets,
      catalogGroups,
      maxPrice,
      minPrice,
      resetAll,
    ]
  )

  const paginationRef = useRef<HTMLDivElement>(null)
  const loaderRef = useRef<HTMLDivElement>(null)

  const loadMore = useCallback(() => {
    const maxAllowed = calculatedTotalPages || 1
    const newPage = Math.min(activePages + 1, maxAllowed)

    if (newPage > activePages && newPage <= maxAllowed) {
      setPage(newPage)
      setActivePages(newPage)

      requestAnimationFrame(() => {
        const pageRef = pagesRefs.current[newPage - 1]
        if (pageRef) {
          pageRef.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })

      setQuery(router, { page: newPage.toString(), totalPages: newPage.toString() }).then()
    }
  }, [activePages, router, calculatedTotalPages])

  const noResults = categoryData?.totalNumProducts === 0

  const allItemsLoaded = calculatedTotalPages ? activePages >= calculatedTotalPages : false

  const pagination = (
    <>
      <Pagination
        pagesCount={activePages}
        // pagesCount={loadedResultsCount ?? 0}
        currentPage={page}
        goToPage={goToPage}
        onLoadMore={loadMore}
        allPagesLoaded={allItemsLoaded}
        moreItemsToShow={allItemsLoaded || !totalResults ? undefined : totalResults - loadedResultsCount}
      />
    </>
  )

  const handleOnPartInView = useCallback((page: number, inView: boolean) => {
    const res = [{ page, inView }]
      .filter((s) => s.inView)
      .filter((s) => !Number.isNaN(s.page))
      .sort((a, b) => a.page - b.page)
      .reverse()
  }, [])

  const selectedBrands = brandsData

  const handleFilterBrandUnselect = (brand: ManufacturerFacet) => {
    toggleBrandFilter(brand)
  }

  const priceRangeSelected = useMemo(() => {
    if (minPrice || maxPrice) {
      const minActualPrice = minPrice || minAvailablePrice || 0
      const maxActualPrice = maxPrice || maxAvailablePrice || 0
      return `${getFormattedPrice(minActualPrice, currency, true)} - ${getFormattedPrice(
        maxActualPrice,
        currency,
        true
      )}`
    }
    return ''
  }, [minPrice, minAvailablePrice, maxPrice, maxAvailablePrice, currency])

  const shipName = useMemo(() => {
    if (catalogId) {
      const [name = ''] = translate(`route.${catalogId}.lbl`).split(' (')
      return name
    }
    return ''
  }, [catalogId, translate])

  const handleCustomFacetUncheck = (facet: FilterValue) => {
    toggleCustom(facet)
  }

  const ProductContainerComponent = useCallback(
    (props: any) => {
      return (
        <ProductsContainer
          {...props}
          className={clsx(
            viewType === 'grid' && isSidePanelOpen && s['side-panel-open-grid-container'],
            'transition-opacity'
            // loading && 'opacity-20'
          )}
        />
      )
    },
    [viewType, ProductsContainer, isSidePanelOpen]
  )
  const onDataStartLoading = useCallback(() => {
    const filtersKey = `${id || ''}_${queryString}_${locale}`

    if (loadingStartRef.current !== filtersKey) {
      loadingStartRef.current = filtersKey
      setLatestLoadedPage(0)
      expectedPagesRef.current = validatedActivePagesRef.current
    }

    setIsFetching(true)
    setCategoryData(null)
  }, [id, queryString, locale])

  const onDataLoadingCancelled = useCallback(() => {
    setIsFetching(false)
  }, [])

  const colSizeWrapper = isLaptop && !noResults ? (isSidePanelOpen ? '8' : '12') : '12'
  const colSizeItem =
    isSidePanelOpen && !isMobile
      ? colSize(['12', '4', '4', '4', '4'], deviceSize)
      : colSize(['12', '6', '4', '3', '3'], deviceSize)
  const col = {
    sm: isSidePanelOpen ? '4' : '3',
    lg: isSidePanelOpen ? '4' : '3',
  }

  const validatedActivePages = useMemo(() => {
    if (calculatedTotalPages) {
      return Math.min(activePages, calculatedTotalPages)
    }
    const MAX_INITIAL_PAGES = 5
    return Math.min(activePages, MAX_INITIAL_PAGES)
  }, [activePages, calculatedTotalPages])

  const validatedActivePagesRef = useRef(validatedActivePages)
  useEffect(() => {
    validatedActivePagesRef.current = validatedActivePages
    if (!expectedPagesRef.current || expectedPagesRef.current === 1) {
      expectedPagesRef.current = validatedActivePages
    }
  }, [validatedActivePages])

  const productsParts = useMemo(() => {
    if (!isRouterReady) {
      return <></>
    }
    return (
      <CwsGrid width="fluid">
        <CwsGridRow>
          {new Array(validatedActivePages).fill(null).map((_, index) => {
            return (
              <ProductsPartMemorized
                key={`products_page_${id || categoryNameOrSearchTerm}_${queryString}_${index}_${locale}`}
                query={queryString as string}
                page={index + 1}
                itemsPerPageValue={itemsPerPageValue}
                onDataStartLoading={onDataStartLoading}
                onDataLoaded={handleDataLoaded}
                onDataLoadingCancelled={onDataLoadingCancelled}
                ProductItemComponent={ProductItem}
                ProductsContainerComponent={ProductContainerComponent}
                id={parseInt(manufacturerId ? '0' : (id as string) ?? '0')}
                manufacturerId={manufacturerId}
                manufacturerIds={manufacturerIds}
                manufacturerName={manufacturerName}
                collectionName={collectionName as string}
                onInViewChange={handleOnPartInView}
                customFacets={customs || []}
                searchTerm={categoryNameOrSearchTerm}
                locale={locale}
                catalogId={catalogId as Catalog}
                minPrice={minPrice}
                maxPrice={maxPrice}
                colSize={colSizeItem}
                ref={(el) => {
                  if (el) {
                    // el.style.background = (index + 1) % 2 ? 'rgba(108, 250, 108, 0.4)' : 'rgba(119, 3, 113, 0.4)'
                    pagesRefs.current[index] = el
                  }
                }}
              />
            )
          })}
        </CwsGridRow>
      </CwsGrid>
    )
  }, [
    id,
    categoryNameOrSearchTerm,
    queryString,
    customs,
    isRouterReady,
    ProductItem,
    itemsPerPageValue,
    ProductContainerComponent,
    validatedActivePages,
    locale,
    minPrice,
    maxPrice,
    colSizeItem,
    catalogId,
    collectionName,
    manufacturerIds,
    onDataLoadingCancelled,
  ])

  const { setCurrentCatalog, handleCatalogSelect, currentCatalog } = useModalState()

  const [noResultsCategoryData, setNoResultsCategoryData] = useState<ProductSearchResult | null>(null)

  const noResultsDefaultPlaceholderCategory = 2492

  useEffect(() => {
    if (noResults && !noResultsCategoryData) {
      axios
        .post<CategoryPagePayload>(`/api/category/${noResultsDefaultPlaceholderCategory}`)
        .then(({ data: { category, banners } }) => {
          setNoResultsCategoryData(category)
        })
        .catch((error) => {
          console.error('Handling not result category fetch', error)
        })
    }
  }, [noResults, noResultsCategoryData])

  const totalNumProductsIsEmpty = useRef<number>(0)

  useEffect(() => {
    if (totalNumProductsIsEmpty.current === 0 || totalNumProductsIsEmpty.current !== categoryData?.totalNumProducts) {
      if (categoryData?.totalNumProducts) {
        totalNumProductsIsEmpty.current = categoryData?.totalNumProducts
      }
    }
  }, [categoryData?.totalNumProducts])
  const isNothingFound = noResults && !jsxData

  return (
    <CwsSection className={clsx(s.wrapper)}>
      <CwsGridRow noGutter>
        <CwsGridCol col="12">
          <CwsGridRow>
            {isLaptop && isSidePanelOpen && !noResults && !!totalNumProductsIsEmpty.current && (
              <CwsGridCol col="4">
                <StickyBox offsetTop={200} offsetBottom={40}>
                  {sidePanelNode}
                </StickyBox>
              </CwsGridCol>
            )}
            {!isLaptop && !noResults && !!totalNumProductsIsEmpty.current && <>{sidePanelNode}</>}

            <CwsAligner>
              <CwsGridCol col={isLaptop && isSidePanelOpen && !isNothingFound ? '8' : '12'}>
                <CwsGridRow>
                  <CwsGridCol col="12" sm="fluid">
                    <div className="flex flex-col space-y-xxl w-full mb-10">
                      {isNothingFound && <NothingFound />}
                      {!!jsxData && (
                        <div>
                          <BannerWrapper
                            jsxString={jsxData}
                            locale={locale ?? ''}
                            catalogId={catalogId}
                            productListIsEmpty={noResults}
                          />
                          {/*<JsxInjection jsx={jsxData} />*/}
                        </div>
                      )}
                      {!noResults && (
                        <>
                          {Boolean(banners.length) && <Banners banners={banners} />}
                          {!!totalNumProductsIsEmpty.current && (
                            <div className="flex flex-col relative">
                              <CategoryTitle
                                title={
                                  manufacturerName ??
                                  categoryNameOrSearchTerm ??
                                  title ??
                                  (translate('w2.category.loading.message') as string)
                                }
                                itemsCount={loading ? 0 : totalNumProductsIsEmpty.current}
                              />
                            </div>
                          )}
                        </>
                      )}
                      {!!totalNumProductsIsEmpty.current && (
                        <Parameter
                          isCategoryPage
                          setItemsPerPageValue={setItemsPerPageValue}
                          itemsPerPageValue={itemsPerPageValue}
                          setIsSidePanelOpen={setIsSidePanelOpen}
                          isSidePanelOpen={isSidePanelOpen}
                          handleSelect={(value) => setSortOrder(KK_ORDER_BY_VALUES[value as OrderKey])}
                          label={translate('search.page.orderby.label') as string}
                          options={orderByOptions}
                        />
                      )}

                      {!!totalNumProductsIsEmpty.current && (
                        <div className="flex flex-row space-x-2">
                          <div className="flex flex-row gap-x-2 gap-y-2 flex-wrap max-w-full">
                            {isGloballyPricelist() && shipName && (
                              <FilterTag
                                hideClose
                                key={'catalog-filter-tag'}
                                filterName={translate('facet.line.ship')}
                                value={shipName}
                              />
                            )}
                            {priceRangeSelected && (
                              <FilterTag
                                key={'price-range-filter-tag'}
                                filterName={translate('cart.confirmation.table.header.price')}
                                value={priceRangeSelected}
                                onClose={() => {
                                  resetPriceRange()
                                }}
                              />
                            )}

                            {selectedBrands?.length === 1 &&
                              selectedBrands?.map((brand) => {
                                return (
                                  <FilterTag
                                    key={brand?.id}
                                    filterName={translate('filters.manufacturers')}
                                    value={brand?.name ?? ''}
                                    onClose={handleFilterBrandUnselect.bind(this, brand)}
                                  />
                                )
                              })}

                            {selectedBrands?.length > 1 && (
                              <div className="flex-col gap-3 h-7 overflow-hidden hover:overflow-auto hover:h-fit bg-midnight text-white">
                                <FilterTag
                                  key={'brands.multiple.badge'}
                                  filterName={translate('filters.manufacturers')}
                                  value={translate('w2.filters.multiple')}
                                  onClose={() => {
                                    selectedBrands.forEach((brand) => handleFilterBrandUnselect(brand))
                                  }}
                                />
                                {selectedBrands?.map((brand) => {
                                  return (
                                    <FilterTag
                                      key={brand?.id}
                                      filterName={translate('filters.manufacturers')}
                                      hideLabel
                                      value={brand?.name ?? ''}
                                      onClose={handleFilterBrandUnselect.bind(this, brand)}
                                    />
                                  )
                                })}
                              </div>
                            )}

                            <CategoryFilterGroup filtersData={customs} onClose={handleCustomFacetUncheck} />
                          </div>
                        </div>
                      )}

                      {latestLoadedPage === 0 && (
                        <div className="flex w-full items-center justify-center" ref={loaderRef}>
                          {loading && (
                            <div className="flex flex-col mx-auto items-center justify-center">
                              <div className="animate-spin">
                                <Loader />
                              </div>
                              <CwsP>{translate('w2.spinner.message')}</CwsP>
                            </div>
                          )}
                        </div>
                      )}

                      <div
                        className={clsx(
                          s['products-contaienr'],
                          'relative',
                          'flex flex-col',
                          viewType === 'grid' ? 'gap-y-[2.5rem]' : 'gap-y-4'
                        )}
                      >
                        {noResults ? (
                          <>
                            {/* <p className="m-auto font-medium text-dark-grey leading-5">{t('faq.error.noresults')}</p> */}
                            {noResultsCategoryData ? (
                              <CwsGrid width="fluid">
                                <CwsGridRow>
                                  {noResultsCategoryData.productArray?.map((product) => (
                                    <CwsGridCol
                                      col={colSize(['12', '6', '4', '3', '3'], deviceSize)}
                                      key={`${id}_noResults_${product.id}`}
                                    >
                                      <ProductCard listName="" product={product} />
                                    </CwsGridCol>
                                  ))}
                                </CwsGridRow>
                              </CwsGrid>
                            ) : (
                              <div className="flex w-full h-[30vh] items-center justify-center absolute top-0 animate-spin">
                                <Loader />
                              </div>
                            )}
                          </>
                        ) : (
                          <>{productsParts}</>
                        )}
                      </div>

                      {latestLoadedPage > 0 && (
                        <div className="flex w-full items-center justify-center" ref={loaderRef}>
                          {loading && (
                            <div className="animate-spin">
                              <Loader />
                            </div>
                          )}
                        </div>
                      )}

                      <span>
                        {typeof window !== 'undefined' && !!pagesTotal && !loading && pagesTotal > 1 && (
                          <InView
                            key="loadMore"
                            as="div"
                            onChange={(inView) => {
                              setEndReached(inView)
                            }}
                            delay={50}
                          >
                            {({ ref }) => (
                              <div className="py-10 border-y border-group-grey" ref={ref}>
                                <div ref={paginationRef}>{pagination}</div>
                              </div>
                            )}
                          </InView>
                        )}
                        {!!pagesTotal && isLaptop && pagesTotal > 1 && !endReached && (
                          <div
                            className={clsx(
                              activePages > 1 && 'fixed',
                              'flex bottom-0 h-16 w-full inset-x-0 z-30 bg-white justify-end shadow-[0_0_16px_0_rgba(0,0,0,0.15)]'
                            )}
                          >
                            <CwsGrid>
                              <CwsGridRow justifyContent="right" verticalAlign="center">
                                <CwsGridCol col={colSizeWrapper}>
                                  <div className="w-full my-auto py-3">
                                    {isSidePanelOpen && <div className="flex-[0_0_304px] mr-6" />}
                                    <div className="flex flex-1">{pagination}</div>
                                  </div>
                                </CwsGridCol>
                              </CwsGridRow>
                            </CwsGrid>
                          </div>
                        )}
                        {categoryDescription && (
                          <div className={clsx(!!pagesTotal && 'border-t', 'py-10 border-b border-group-grey m-0')}>
                            <CwsSpan className="cws-text--color-midnight">{categoryDescription}</CwsSpan>
                          </div>
                        )}
                      </span>
                    </div>
                  </CwsGridCol>
                </CwsGridRow>
              </CwsGridCol>
            </CwsAligner>
          </CwsGridRow>
        </CwsGridCol>
      </CwsGridRow>

      {isGloballyPricelist() && (
        <CwsModal hideCloseButton id={MODAL_ID}>
          <CwsModalBody>
            <CwsH2>{translate('select.shop.confirm.title')}</CwsH2>
            <CwsP>{translate('select.shop.confirm.store1.store1')}</CwsP>
          </CwsModalBody>
          <CwsModalFooter>
            <CwsGrid width="fluid">
              <CwsGridRow noGutter>
                <CwsGridCol col="6" className="text-start">
                  <CwsButton
                    variant="empty"
                    onClick={() => {
                      modal.addEventModalHide(MODAL_ID)
                      setCurrentCatalog(null)
                    }}
                  >
                    {translate('select.shop.confirm.cancel')}
                  </CwsButton>
                </CwsGridCol>
                <CwsGridCol col="6" className="text-end">
                  <CwsButton
                    onClick={async () => {
                      await handleCatalogSelect()
                      modal.addEventModalHide(MODAL_ID)
                    }}
                  >
                    {translate('select.shop.confirm.continue')}
                  </CwsButton>
                </CwsGridCol>
              </CwsGridRow>
            </CwsGrid>
          </CwsModalFooter>
        </CwsModal>
      )}
    </CwsSection>
  )
}

export default CategoryView

const MemorizedSidePanel = memo(SidePanelWrapper, (prevProps, nextProps) => {
  return (
    prevProps.categoryId === nextProps.categoryId &&
    prevProps.currentMaxPrice === nextProps.currentMaxPrice &&
    prevProps.currentMinPrice === nextProps.currentMinPrice &&
    prevProps.collectionName === nextProps.collectionName &&
    prevProps.manufacturerFacets.length === nextProps.manufacturerFacets.length &&
    prevProps.customFacets.length === nextProps.categoryFacets.length &&
    prevProps.minAvailablePrice === nextProps.minAvailablePrice &&
    prevProps.maxAvailablePrice === nextProps.maxAvailablePrice &&
    prevProps.catalogGroups === nextProps.catalogGroups
  )
})
